/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useState} from "react"
import {Checkbox, Col, Row} from "antd"
import {courseService} from "services"
import {KlassDropdown} from "components/Select"
import {BaseDatePicker} from "components/DateTimePicker"
import {BaseButton} from "components/buttons"
import {BaseNewFilter} from "uiKit"
import CourseGradeTable from "./CourseGradeTable"
import styles from "./CourseGradesTab.module.css"
import AddCourseGrade from "./AddCourseGrade"
import {getFullName, handleError} from "helpers"
import {useGradingTemplates, useModel} from "hooks"
import CampusSelect from "components/CampusSelect"
import {GradingSystemItem} from "types/gradingSystem"

const DROPDOWN_STYLE = {
    control: (provided) => {
        return {
            ...provided,
            borderRadius: 12,
            minHeight: 44,
            borderWidth: 2,
            borderColor: "#1e90ff",
            fontSize: 14,
            fontWeight: "600"
        }
    }
}

export enum GradingItemsFilter {
    All = "all",
    AffectGPA = "affect_gpa",
    NotAffectGPA = "not_affect_gpa"
}

export enum SAPAlertFilter {
    All = "all",
    Pass = "pass",
    Fail = "fail"
}

const optionFilterGradingItems = [
    {
        name: "ALL",
        value: GradingItemsFilter.All
    },
    {
        name: "AFFECT GPA",
        value: GradingItemsFilter.AffectGPA
    },
    {
        name: "NOT AFFECT GPA",
        value: GradingItemsFilter.NotAffectGPA
    }
]
const optionFilterSapAlerts = [
    {
        name: "ALL",
        value: SAPAlertFilter.All
    },
    {
        name: "PASS",
        value: SAPAlertFilter.Pass
    },
    {
        name: "FAIL",
        value: SAPAlertFilter.Fail
    }
]

export function CourseGradesTab(props) {
    const {courseId, courseInfo, termId, scheduleId, isActive = true} = props
    const [defaultGradingTemplate, setDefaultGradingTemplate] = useState<GradingSystemItem>()
    const [students, setStudents] = useState([])
    const model = useModel()
    const [listMode, setListMode] = useState(false)
    const [filter, setFilter] = useState({
        campus: null,
        startDate: null,
        affectGpa: optionFilterGradingItems[0],
        spaAlerts: optionFilterSapAlerts[0],
        missingGrades: false
    })
    const [dataFilter, setDataFilter] = useState({
        campus: null,
        startDate: null,
        affectGpa: optionFilterGradingItems[0],
        spaAlerts: optionFilterSapAlerts[0],
        missingGrades: false
    })
    const [isAddGrade, setIsAddGrade] = useState(false)
    const [search, setSearch] = useState<string>("")
    const {data: gradingTemplates} = useGradingTemplates({
        gradingTemplateIds: courseInfo?.gradingTemplateIds ?? []
    })

    useEffect(() => {
        if (gradingTemplates.length) {
            setDefaultGradingTemplate(gradingTemplates[0])
        }
    }, [gradingTemplates])

    const getListStudent = async () => {
        try {
            const params: any = {
                filter: {courseIds: [courseId]},
                range: {page: 1, pageSize: 500}
            }
            if (termId) {
                params.filter.termIds = [termId]
            }
            const profileId = model.profileId
            if (!model.isStaffOrAdmin() && profileId) {
                params.filter.studentProfileIds = [profileId]
            }
            if (scheduleId) {
                params.filter.scheduleId = scheduleId
            }
            const {data: studentStats} = await courseService.listInstructionalStudents(params)

            const data = studentStats.map((item: any) => {
                item.id = item.student.profileId
                item.profileId = item.student.profileId
                item.fullname = getFullName(item.student)
                item.isMakeup = !!item.student.isMakeup
                item.isDropped = !!item.student.isDropped
                item.droppedDate = item.student.droppedDate
                item.addDropDate = item.student.addDropDate
                item.addDropHours = item.student.addDropHours
                item.addDropVariant = item.student.addDropVariant
                item.student.gradingTemplate = item.student.gradingTemplateTitle
                    ? {
                          title: item.student.gradingTemplateTitle,
                          id: item.student.gradingTemplateId
                      }
                    : gradingTemplates[0]
                return item
            })
            setStudents(data)
        } catch (e) {
            handleError(e)
        }
    }

    useEffect(() => {
        if (isActive && gradingTemplates.length) {
            getListStudent()
        }
    }, [termId, courseId, scheduleId, gradingTemplates, isActive])

    const onChangeFilter = (key, value) => {
        const newFilter = {...filter}
        newFilter[key] = value
        setFilter(newFilter)
    }

    const onApplyFilter = () => {
        setDataFilter(filter)
    }

    const onClickClearFilter = () => {
        setFilter({
            campus: null,
            startDate: null,
            affectGpa: optionFilterGradingItems[0],
            spaAlerts: optionFilterSapAlerts[0],
            missingGrades: false
        })
        setDataFilter({
            campus: null,
            startDate: null,
            affectGpa: optionFilterGradingItems[0],
            spaAlerts: optionFilterSapAlerts[0],
            missingGrades: false
        })
    }

    const onSearchInput = (input) => {
        setSearch(input)
    }

    const cancelAddGrade = useCallback(() => {
        setIsAddGrade(false)
    }, [])

    const addGrade = useCallback(() => {
        setIsAddGrade(true)
    }, [])

    const onClickChangeMode = useCallback(() => {
        setListMode(!listMode)
    }, [listMode])

    const onChangeStartDate = useCallback((value: any, text: string) => {
        onChangeFilter("startDate", value)
    }, [])

    const renderRightFilter = () => {
        return (
            <div className={styles.actionWrap}>
                <BaseButton
                    onClick={onClickChangeMode}
                    className={styles.actionChangeMode}
                    title={!listMode ? "List Mode" : "Table Mode"}
                    variant="secondary"
                />
                <BaseButton title="Export" variant="secondary" />
                <BaseButton onClick={addGrade} className={styles.actionAdd} title="Add" variant="primary" />
            </div>
        )
    }

    if (isAddGrade) {
        return (
            <AddCourseGrade
                courseInfo={courseInfo}
                defaultGradingTemplate={defaultGradingTemplate}
                gradingTemplates={gradingTemplates}
                setDefaultGradingTemplate={setDefaultGradingTemplate}
                students={students}
                courseId={courseId}
                scheduleId={scheduleId}
                termId={termId}
                cancelAddGrade={cancelAddGrade}
            />
        )
    }
    return (
        <div>
            <BaseNewFilter
                filter={filter}
                searchValue={search}
                onClick={onApplyFilter}
                onClickClear={onClickClearFilter}
                onSearchInput={onSearchInput}
                renderRightFilter={() => renderRightFilter()}>
                <Row gutter={[40, 24]}>
                    <Col span={12}>
                        <BaseDatePicker
                            value={filter.startDate}
                            onChange={onChangeStartDate}
                            placeholder="Start Date"
                        />
                    </Col>
                    <Col span={12}>
                        <CampusSelect
                            isMulti
                            placeholder="Campus"
                            value={filter.campus}
                            onChange={(newValue) => onChangeFilter("campus", newValue)}
                        />
                    </Col>
                    <Col span={12}>
                        <div className={styles.filterRow}>
                            <span className={styles.filterTitle}>GRADING ITEMS</span>
                            <KlassDropdown
                                stylesCustom={DROPDOWN_STYLE}
                                onChange={(value) => {
                                    onChangeFilter("affectGpa", value)
                                }}
                                options={optionFilterGradingItems}
                                value={filter.affectGpa}
                                valueKey="value"
                                labelKey="name"
                                placeholder="Select"
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.filterItem}>
                            <Checkbox
                                checked={!!filter.missingGrades}
                                onChange={(event) => {
                                    onChangeFilter("missingGrades", event.target.checked)
                                }}
                            />
                            <span className={styles.dataFilter} style={{marginLeft: 8}}>
                                MISSING GRADES
                            </span>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.filterRow}>
                            <span className={styles.filterTitle}>SAP ALERTS</span>
                            <KlassDropdown
                                stylesCustom={DROPDOWN_STYLE}
                                onChange={(value) => {
                                    onChangeFilter("spaAlerts", value)
                                }}
                                options={optionFilterSapAlerts}
                                value={filter.spaAlerts}
                                valueKey="value"
                                labelKey="name"
                                placeholder="Select"
                            />
                        </div>
                    </Col>
                </Row>
            </BaseNewFilter>
            <CourseGradeTable
                courseInfo={courseInfo}
                scheduleId={scheduleId}
                termIds={[termId]}
                filter={dataFilter}
                search={search}
                getListStudent={getListStudent}
                gradingTemplates={gradingTemplates}
                defaultGradingTemplate={defaultGradingTemplate}
                listMode={listMode}
                students={students}
                courseId={courseId}
            />
        </div>
    )
}
